import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { money } from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Sparkline, BarChart, Donut } from "../../components/Sparkline";
import { Button } from "../../components/ui/button";
import { useSupplier } from "../../context/SupplierContext";
import BuyerStorefrontHome from "./BuyerStorefrontHome";
import CrossSupplierActionPanel from "../../components/CrossSupplierActionPanel";
import PendingInvitesCard from "../../components/PendingInvitesCard";
import {
  ShoppingBag, CreditCard, Receipt, Truck, AlertTriangle, ArrowUpRight, TrendingUp,
  FileText, Package, MessageSquare, BarChart3, Activity, AlertCircle, Sparkles,
  Inbox, Banknote,
} from "lucide-react";

const ACTIVITY_ICON = { order: ShoppingBag, paid: Receipt, overdue: AlertCircle, delivered: Truck };

export default function BuyerDashboard() {
  const nav = useNavigate();
  const { active: activeSupplier } = useSupplier();

  // When the buyer is "inside" a specific seller's portal, render the
  // dedicated storefront home (seller-branded hero + scoped data) instead
  // of the cross-seller verqo dashboard. Per product directive: while in
  // a seller's storefront, hide all platform/hub features.
  if (activeSupplier) return <BuyerStorefrontHome />;

  return <BuyerHubDashboard nav={nav} activeSupplier={activeSupplier}/>;
}

function BuyerHubDashboard({ nav, activeSupplier }) {
  const [d, setD] = useState(null);
  const [hist, setHist] = useState(null);
  const [trend, setTrend] = useState(null);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [supplierFeatures, setSupplierFeatures] = useState({ catalog_enabled: false, rfq_enabled: false });
  const [hubSummary, setHubSummary] = useState(null);
  const [me, setMe] = useState(null);   // Jun 2026 — for the public-profile nudge

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/buyer"),
      api.get("/scoring/history"),
      api.get("/spend/trend"),
      api.get("/orders"),
      api.get("/invoices"),
      api.get("/shipping"),
      api.get("/activity"),
      api.get("/inventory/alerts"),
      api.get("/buyer/supplier-features").catch(() => ({ data: null })),
      api.get("/buyer/hub/summary").catch(() => ({ data: null })),
      api.get("/auth/me").catch(() => ({ data: null })),
    ]).then(([a, h, t, o, i, s, ac, al, sf, hs, mm]) => {
      setD(a.data); setHist(h.data); setTrend(t.data);
      setOrders(o.data); setInvoices(i.data); setShipments(s.data);
      setActivity(ac.data); setAlerts(al.data);
      if (sf?.data) setSupplierFeatures(sf.data);
      if (hs?.data) setHubSummary(hs.data);
      if (mm?.data) setMe(mm.data);
    }).catch(() => {});
  }, []);

  // Catalog browsing is currently disabled (the Catalog app is "coming soon").
  // Until it ships, every order starts with a PO upload — no Browse-catalog
  // CTA renders anywhere. When the catalog ships, restore workspace-aware
  // logic from git history.
  const supplierCatalogState = useMemo(() => {
    return { showBrowse: false, showUpload: true };
  }, []);

  if (!d) return <div className="p-8 text-foreground/60">Loading…</div>;
  const k = d.kpis;
  const f = d.fulfillment;
  const dueSoon = invoices.filter(i => i.status !== "paid").slice(0, 4);
  const inTransit = shipments.filter(s => ["booked", "picked_up", "in_transit"].includes(s.status)).slice(0, 4);
  const recentOrders = orders.slice(0, 6);
  const greeting = `${d.greeting_action_items.new_orders} new order${d.greeting_action_items.new_orders!==1?'s':''}, ${d.greeting_action_items.in_transit} shipment${d.greeting_action_items.in_transit!==1?'s':''} in transit, ${d.greeting_action_items.due_soon} invoice${d.greeting_action_items.due_soon!==1?'s':''} due soon.`;

  const overdueInv = invoices.find(i => i.status === "overdue");

  // Hub-mode buyer (no specific supplier active) — surface the verqo Hub
  // features prominently. The whole point: even uninvited buyers get
  // value on day one.
  const showHubCards = !activeSupplier;

  return (
    <>
      <PageHeader title="Good morning, Alex 👋" subtitle={greeting}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={()=>nav("/buyer/apps")} className="rounded-full"><Sparkles className="w-4 h-4 mr-1.5"/>Apps</Button>
            {supplierCatalogState.showBrowse && (
              <Button onClick={()=>nav("/buyer/catalog")} className="rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="dash-shop-cta">
                <ShoppingBag className="w-4 h-4 mr-1.5"/>Browse catalog
              </Button>
            )}
            {supplierCatalogState.showUpload && (
              <Button
                onClick={()=>nav("/buyer/po-generator")}
                className={supplierCatalogState.showBrowse
                  ? "rounded-full"
                  : "rounded-full bg-foreground text-background hover:bg-foreground/90"}
                variant={supplierCatalogState.showBrowse ? "outline" : "default"}
                data-testid="dash-upload-po-cta">
                <FileText className="w-4 h-4 mr-1.5"/>Upload PO
              </Button>
            )}
          </div>
        }/>

      <div className="p-8 space-y-6">

        {/* Iter 161 · Pending supplier invites — first thing buyer sees on
            the dashboard if any sellers have invited them. Renders nothing
            when invites array is empty. */}
        <PendingInvitesCard/>

        {/* Iter 157 · Phase 5 — Cross-supplier action panel.
            In Hub mode, surfaces the top 5 urgent actions (overdue
            invoices, ready-to-ship orders, out-for-delivery shipments)
            across EVERY connected supplier. Each row deep-links and
            auto-switches the buyer's supplier context in one click. */}
        <CrossSupplierActionPanel/>

        {/* Jun 2026 — Public-profile completeness nudge.
            Renders when the buyer has filled in <60% of the public
            profile fields (founded_year, website, categories, social,
            store_type, revenue_band, geographic_reach). Drives organic
            adoption of the seller-facing buyer card. */}
        {(() => {
          if (!me) return null;
          const filledFields = [
            me.founded_year,
            me.website,
            (me.categories || []).length > 0 || undefined,
            me.social && (me.social.linkedin || me.social.instagram || me.social.twitter) ? true : undefined,
            me.store_type,
            me.revenue_band,
            me.geographic_reach,
          ].filter(v => v != null && v !== "");
          const pct = Math.round((filledFields.length / 7) * 100);
          if (pct >= 60) return null;
          return (
            <div data-testid="bpp-nudge"
                 className="rounded-2xl border border-sky-200 bg-sky-50/70 p-5 flex items-start gap-4">
              <div className="relative w-12 h-12 shrink-0 text-sky-700">
                <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                  <circle cx="18" cy="18" r="16" fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="3"/>
                  <circle cx="18" cy="18" r="16" fill="none" stroke="currentColor"
                    strokeWidth="3" strokeLinecap="round"
                    strokeDasharray={`${(pct / 100) * 100.5} 100.5`}/>
                </svg>
                <div className="absolute inset-0 grid place-items-center mono text-[11px] font-semibold tabular-nums">
                  {pct}%
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-sky-900/70 font-medium">
                  Help sellers trust you faster
                </div>
                <div className="text-sky-900 font-medium mt-1">
                  Your profile is <strong>{pct}%</strong> complete — completed profiles get faster term approvals.
                </div>
                <div className="text-[12.5px] text-sky-900/70 mt-1">
                  Add your founded year, categories, social handles and a brand-type so sellers know who they're dealing with at a glance.
                </div>
              </div>
              <Button onClick={() => nav("/buyer/settings#public-profile")}
                data-testid="bpp-nudge-cta"
                className="rounded-full bg-sky-900 text-sky-50 hover:bg-sky-900/90 shrink-0">
                Complete profile<ArrowUpRight className="w-3 h-3 ml-1"/>
              </Button>
            </div>
          );
        })()}

        {/* High-priority alert */}
        {overdueInv && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 flex items-start gap-4" data-testid="action-banner">
            <div className="w-10 h-10 rounded-full bg-amber-200 grid place-items-center shrink-0"><AlertTriangle className="w-5 h-5 text-amber-800"/></div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-amber-900/70 font-medium">Action needed</div>
              <div className="text-amber-900 font-medium mt-1">Invoice <span className="mono">{overdueInv.code}</span> for <span className="display">{money(overdueInv.amount)}</span> is overdue from {overdueInv.seller_name}.</div>
            </div>
            <Button onClick={()=>nav("/buyer/orders")} className="rounded-full bg-amber-900 text-amber-50 hover:bg-amber-900/90">Pay now <ArrowUpRight className="w-3 h-3 ml-1"/></Button>
          </div>
        )}

        {/* KPIs row 1 — same 6 slots in every mode. Values switch when the
            buyer has no active supplier (Hub mode), so the dashboard stays
            useful across modes without rearranging the layout. */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dashboard-kpis">
          {(showHubCards && hubSummary ? [
            { label: "Trade Score",      val: hubSummary.score || "—",                              hint: hubSummary.score ? "Verqo Trade Score · BETA" : "Connect a bank to compute", tone: "ink", icon: TrendingUp },
            { label: "POs in Inbox",     val: hubSummary.external_pos,                              hint: `${money(hubSummary.external_pos_total)} tracked`,           icon: FileText },
            { label: "Invoices on file", val: hubSummary.external_invoices,                         hint: `${money(hubSummary.external_invoices_total)} total`,         icon: Receipt },
            { label: "Financed",         val: hubSummary.external_invoices_financed,                hint: `${money(hubSummary.external_invoices_financed_total)} cash`, icon: Truck },
            { label: "Invites sent",     val: hubSummary.invites_sent,                              hint: `${hubSummary.invites_joined} joined`,                       icon: CreditCard },
            { label: "Suppliers reach",  val: (hubSummary.invites_sent || 0) + (hubSummary.invites_joined || 0), hint: "On verqo + off-platform",                       icon: Package },
          ] : [
            { label: "Spend MTD", val: money(k.spend_mtd), hint: `${k.spend_change_pct >= 0 ? "↑" : "↓"} ${Math.abs(k.spend_change_pct)}%`, tone: "ink", icon: TrendingUp },
            { label: "Active orders", val: k.active_orders, hint: `${k.new_today} new today`, icon: FileText },
            { label: "Outstanding", val: k.outstanding_invoices, hint: `${money(k.outstanding_amount)} due`, icon: Receipt },
            { label: "In-transit", val: k.in_transit, hint: `${k.arriving_soon} arriving soon`, icon: Truck },
            { label: "Credit avail.", val: money(k.credit_available), hint: `${k.credit_terms} active`, icon: CreditCard },
            { label: "SKUs in stock", val: k.skus_in_stock, hint: `${k.low_stock} low stock`, icon: Package },
          ]).map((s, i) => (
            <div key={i} className={`rounded-xl p-4 relative ${s.tone === "ink" ? "card-ink" : "bg-white border border-border"}`}>
              <s.icon className={`absolute top-4 right-4 w-3.5 h-3.5 ${s.tone === "ink" ? "text-accent/70" : "text-foreground/30"}`}/>
              <div className={`text-[10px] uppercase tracking-widest ${s.tone === "ink" ? "text-accent/80" : "text-foreground/50"}`}>{s.label}</div>
              <div className="display text-xl mt-2">{s.val}</div>
              <div className={`text-[10px] mt-0.5 ${s.tone === "ink" ? "text-background/60" : "text-foreground/55"}`}>{s.hint}</div>
            </div>
          ))}
        </div>

        {/* verqo Hub — Mode 2 positioning: works without supplier opt-in */}
        {showHubCards && (
          <section data-testid="hub-cards">
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50 mb-2.5">verqo Hub · run wholesale ops with or without your suppliers</div>
            <div className="grid md:grid-cols-3 gap-3">
              <Link to="/buyer/score"
                className="rounded-2xl border border-border bg-white p-5 hover:border-foreground/30 transition group" data-testid="hub-card-score">
                <div className="flex items-center justify-between">
                  <TrendingUp className="w-5 h-5 text-foreground/70"/>
                  <ArrowUpRight className="w-4 h-4 text-foreground/40 group-hover:text-foreground"/>
                </div>
                <div className="display text-lg mt-3">Your Verqo Trade Score</div>
                <div className="text-xs text-foreground/60 mt-1">Free B2B trade passport · BETA. Carries across every supplier you work with — verqo or off.</div>
              </Link>
              <Link to="/buyer/po-inbox"
                className="rounded-2xl border border-border bg-white p-5 hover:border-foreground/30 transition group" data-testid="hub-card-po-inbox">
                <div className="flex items-center justify-between">
                  <Inbox className="w-5 h-5 text-foreground/70"/>
                  <ArrowUpRight className="w-4 h-4 text-foreground/40 group-hover:text-foreground"/>
                </div>
                <div className="display text-lg mt-3">Send a PO</div>
                <div className="text-xs text-foreground/60 mt-1">To any supplier — they get a public link to acknowledge, ship, and invoice. No account needed.</div>
              </Link>
              <Link to="/buyer/hub/financing"
                className="rounded-2xl border border-border bg-white p-5 hover:border-foreground/30 transition group" data-testid="hub-card-financing">
                <div className="flex items-center justify-between">
                  <Banknote className="w-5 h-5 text-foreground/70"/>
                  <ArrowUpRight className="w-4 h-4 text-foreground/40 group-hover:text-foreground"/>
                </div>
                <div className="display text-lg mt-3">Finance an invoice</div>
                <div className="text-xs text-foreground/60 mt-1">Any unpaid invoice from anyone. We pay the supplier in 24h, you repay at the due date.</div>
              </Link>
            </div>
          </section>
        )}

        {/* Charts row */}
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Spend trend (6 mo) */}
          <div className="lg:col-span-2 rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="display text-lg">Spend trend</h2>
                <p className="text-xs text-foreground/60">Last 6 months · margin estimate</p>
              </div>
              <div className="flex gap-3 text-xs">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-foreground"/>Spend</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-accent"/>Est. margin</span>
              </div>
            </div>
            {trend?.months && <div className="h-44"><BarChart data={trend.months}/></div>}
          </div>

          {/* Order Fulfillment */}
          <div className="rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="display text-lg">Fulfillment</h2>
              <span className="chip bg-accent/30 border border-accent/40">{f.on_time_pct}% on-time</span>
            </div>
            <div className="text-xs text-foreground/60 mb-3">{f.total} orders</div>
            <div className="space-y-3 mt-4 text-sm">
              {[
                { lbl: "Delivered", val: f.delivered, pct: f.total ? (f.delivered/f.total*100) : 0, color: "bg-foreground" },
                { lbl: "In transit", val: f.in_transit, pct: f.total ? (f.in_transit/f.total*100) : 0, color: "bg-foreground/60" },
                { lbl: "Processing", val: f.processing, pct: f.total ? (f.processing/f.total*100) : 0, color: "bg-foreground/40" },
                { lbl: "Delayed", val: f.delayed, pct: f.total ? (f.delayed/f.total*100) : 0, color: "bg-amber-500" },
              ].map((row, i) => (
                <div key={i}>
                  <div className="flex justify-between text-xs"><span>{row.lbl}</span><span className="mono">{row.val} · {Math.round(row.pct)}%</span></div>
                  <div className="mt-1 h-1.5 bg-secondary rounded-full overflow-hidden"><div className={`h-full ${row.color}`} style={{width: `${row.pct}%`}}/></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick actions strip — entries are filtered by Boolean() so we can
            conditionally surface multiple ordering paths in hub mode. */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
          {[
            // Catalog tile: shown in hub mode (any supplier with catalog) and
            // in supplier-scoped mode when that supplier has catalog.
            supplierCatalogState.showBrowse && {
              label: "New order", icon: ShoppingBag, to: "/buyer/orders#journey", hint: "Place an order",
            },
            // Upload PO tile: shown ALWAYS in hub mode (a buyer might want to
            // PO any connected supplier, with or without catalog) and in
            // supplier-scoped mode when that supplier has no catalog.
            supplierCatalogState.showUpload && {
              label: "Upload PO", icon: FileText, to: "/buyer/po-generator", hint: "PO / Excel",
            },
            { label: "Pay invoices",        icon: Receipt,        to: "/buyer/orders",        badge: k.outstanding_invoices, hint: "Open" },
            supplierFeatures.rfq_enabled
              ? { label: "Submit RFQ",      icon: MessageSquare,  to: "/buyer/rfq",           hint: "Open" }
              : { label: "Suppliers",       icon: Package,        to: "/buyer/suppliers",     hint: "Open" },
            { label: "Track shipments",     icon: Truck,          to: "/buyer/orders",        badge: k.in_transit, hint: "Open" },
            { label: "View reports",        icon: BarChart3,      to: "/buyer/reports",       hint: "Open" },
          ].filter(Boolean).map((a) => (
            <button key={a.label} onClick={() => nav(a.to)}
              data-testid={`qa-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-xl bg-white border border-border p-3.5 text-left hover:border-foreground/40 hover:bg-secondary/40 transition-all group flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-secondary group-hover:bg-foreground group-hover:text-background grid place-items-center transition-colors shrink-0">
                <a.icon className="w-4 h-4"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{a.label}</div>
                <div className="text-[10px] text-foreground/50">{a.hint || "Open"}</div>
              </div>
              {a.badge > 0 && <span className="chip bg-accent text-foreground border border-accent shrink-0">{a.badge}</span>}
            </button>
          ))}
        </div>

        {/* Recent orders + Invoices due + In-transit */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="display text-lg">Recent orders</h2>
                <p className="text-xs text-foreground/60">Last 7 days</p>
              </div>
              <Link to="/buyer/orders" className="text-xs text-foreground/60 hover:text-foreground inline-flex items-center">View all <ArrowUpRight className="w-3 h-3 ml-0.5"/></Link>
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-foreground/50">
                <tr><th className="pb-3 font-medium">Order</th><th className="pb-3 font-medium">Brand</th><th className="pb-3 font-medium">Units</th><th className="pb-3 font-medium">Total</th><th className="pb-3 font-medium">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {recentOrders.map(o => (
                  <tr key={o.id}>
                    <td className="py-3 mono text-foreground/70">{o.code}</td>
                    <td className="py-3">{o.seller_name}</td>
                    <td className="py-3 text-xs">{(o.items || []).reduce((s, i) => s + (i.qty || 0), 0)}</td>
                    <td className="py-3 display">{money(o.total)}</td>
                    <td className="py-3"><span className="chip bg-secondary">{o.status}</span></td>
                  </tr>
                ))}
                {recentOrders.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-foreground/60">No orders yet</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="space-y-5">
            <div className="rounded-2xl bg-white border border-border p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="display text-base">Invoices due</h3>
                  <p className="text-[10px] text-foreground/50">{dueSoon.length} pending</p>
                </div>
                <Link to="/buyer/orders" className="text-xs text-foreground/60 hover:text-foreground">Pay <ArrowUpRight className="w-3 h-3 inline ml-0.5"/></Link>
              </div>
              <div className="divide-y divide-border">
                {dueSoon.map(i => (
                  <div key={i.id} className="py-2.5 flex justify-between items-center">
                    <div className="min-w-0">
                      <div className="text-sm font-medium mono">{i.code}</div>
                      <div className="text-[10px] text-foreground/60 truncate">{i.seller_name} · {new Date(i.due_date).toLocaleDateString(undefined,{month:'short',day:'numeric'})}</div>
                    </div>
                    <span className="display text-sm">{money(i.amount)}</span>
                  </div>
                ))}
                {dueSoon.length === 0 && <div className="py-6 text-center text-foreground/60 text-xs">All caught up 🎉</div>}
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-border p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="display text-base">In-transit</h3>
                  <p className="text-[10px] text-foreground/50">{inTransit.length} shipping</p>
                </div>
                <Link to="/buyer/orders" className="text-xs text-foreground/60 hover:text-foreground">Track <ArrowUpRight className="w-3 h-3 inline ml-0.5"/></Link>
              </div>
              <div className="divide-y divide-border">
                {inTransit.map(s => (
                  <div key={s.id} className="py-2.5 flex justify-between items-center">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{s.carrier}</div>
                      <div className="text-[10px] mono text-foreground/60 truncate">{s.tracking}</div>
                    </div>
                    <span className="chip bg-secondary text-[10px]">{s.status}</span>
                  </div>
                ))}
                {inTransit.length === 0 && <div className="py-6 text-center text-foreground/60 text-xs">No shipments</div>}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom row: Top suppliers + Inventory alerts + Activity */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="display text-base">Top suppliers</h2>
              <Link to="/buyer/suppliers" className="text-xs text-foreground/60 hover:text-foreground">All <ArrowUpRight className="w-3 h-3 inline ml-0.5"/></Link>
            </div>
            <div className="space-y-3">
              {(trend?.top_sellers || []).map((s, i) => {
                const max = trend?.top_sellers?.[0]?.value || trend?.top_sellers?.[0]?.amount || 1;
                const v = s.amount || s.value;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="truncate">{s.name}</span>
                      <span className="display text-sm">{money(v)}</span>
                    </div>
                    <div className="h-1.5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-foreground" style={{width: `${(v/max)*100}%`}}/></div>
                  </div>
                );
              })}
              {(!trend?.top_sellers || trend.top_sellers.length === 0) && <div className="text-foreground/60 text-sm py-4 text-center">No data yet</div>}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="display text-base">Inventory alerts</h2>
              <Link to="/buyer/inventory" className="text-xs text-foreground/60 hover:text-foreground">All <ArrowUpRight className="w-3 h-3 inline ml-0.5"/></Link>
            </div>
            <div className="space-y-2.5">
              {alerts.slice(0, 5).map((a, i) => (
                <div key={i} className="flex items-start gap-3 py-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${a.severity === "critical" ? "bg-rose-500" : "bg-amber-500"}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{a.name}</div>
                    <div className="text-[10px] text-foreground/60">{a.on_hand} units · reorder pt {a.reorder_point}</div>
                  </div>
                  <button onClick={()=>nav("/buyer/orders#journey")} className="text-[10px] mono text-foreground/60 hover:text-foreground shrink-0">Reorder ↗</button>
                </div>
              ))}
              {alerts.length === 0 && <div className="text-foreground/60 text-sm py-4 text-center">All stocks healthy</div>}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="display text-base">Activity</h2>
              <Activity className="w-4 h-4 text-foreground/40"/>
            </div>
            <div className="space-y-3">
              {activity.slice(0, 6).map((a, i) => {
                const I = ACTIVITY_ICON[a.type] || Activity;
                return (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${a.type === "overdue" ? "bg-rose-50 text-rose-600" : "bg-secondary"}`}>
                      <I className="w-3.5 h-3.5"/>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium truncate">{a.title}</div>
                      <div className="text-[10px] text-foreground/55 truncate">{a.subtitle} · {new Date(a.date).toLocaleDateString()}</div>
                    </div>
                  </div>
                );
              })}
              {activity.length === 0 && <div className="text-foreground/60 text-sm py-4 text-center">No recent activity</div>}
            </div>
          </div>
        </div>

        {/* Score + Credit summary */}
        <div className="grid md:grid-cols-2 gap-5">
          <div className="card-ink rounded-2xl p-6 relative overflow-hidden">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-accent/15 blur-3xl"/>
            <div className="relative flex items-center justify-between gap-6">
              <div>
                <div className="text-xs mono uppercase tracking-widest text-accent/80">Verqo Trade Score</div>
                <div className="display text-5xl mt-2">{d.score}</div>
                <div className="text-xs text-background/70 mt-1">{d.rating} {hist?.delta_30d != null && <span className="ml-1">· {hist.delta_30d >= 0 ? "+" : ""}{hist.delta_30d} this month</span>}</div>
              </div>
              <div className="w-32 h-12">
                {hist?.points && <Sparkline data={hist.points.map(p => ({ value: p.score }))} stroke="#E8FF3A" fill="rgba(232,255,58,0.15)" height={48}/>}
              </div>
            </div>
            <Link to="/buyer/score" className="text-xs mono text-accent/80 hover:text-accent inline-flex items-center mt-4">View breakdown <ArrowUpRight className="w-3 h-3 ml-1"/></Link>
          </div>

          <div className="rounded-2xl bg-white border border-border p-6 flex items-center gap-5">
            <Donut value={d.credit_pct} max={100} size={80} stroke={9}/>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-foreground/50">Credit line</div>
              <div className="display text-3xl mt-1">{money(d.credit_limit - d.credit_used)}</div>
              <div className="text-xs text-foreground/60 mt-0.5">available · {d.credit_pct}% utilized</div>
              <Link to="/buyer/capital" className="text-xs mono text-foreground/60 hover:text-foreground inline-flex items-center mt-2">Manage <ArrowUpRight className="w-3 h-3 ml-1"/></Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
